/**
 * /companies: employers that send the firm their employees' cases. Individual
 * clients stay under Clients.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Download, Plus, Sparkles } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DisplayOptions } from "@/components/ui/display-options";
import { EmptyState } from "@/components/ui/empty-state";
import { ListSearch, ListToolbar } from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { usePracticeTeamMembers } from "@/hooks/usePracticeTeamMembers";
import { cn } from "@/lib/utils";

import { useFirmPermissions } from "@/hooks/useFirmPermissions";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";

import { useTimeStatus } from "../../timeTracking/queries";
import { formatMoney } from "../../timeTracking/utils";
import { formatLabel } from "../billingModel";
import { CompanyMark } from "../components/CompanyMark";
import { ImportDialog } from "../components/ImportDialog";
import { NewCompanyDialog } from "../components/NewCompanyDialog";
import { useCompanies, useMigrationProposals } from "../queries";
import type { CompanyListRow } from "../types";
import { COMPANIES_REVIEW_PATH, companyPath } from "../routes";
import {
  entitySummary,
  formatDeadline,
  initialsOf,
  isSoon,
  sortCompanies,
  type CompanySort
} from "../utils";

const SORT_OPTIONS: { value: CompanySort; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "name", label: "Name (A–Z)" },
  { value: "open", label: "Open Cases" },
  { value: "deadline", label: "Next Deadline" }
];

const OPTIONAL_COLUMNS = [
  { id: "open", label: "Open Cases" },
  { id: "deadline", label: "Next Deadline" },
  { id: "clientNo", label: "Client No." },
  { id: "export", label: "Export" },
  { id: "unbilled", label: "Unbilled" },
  { id: "lead", label: "Lead" }
];

export const CompaniesPage: FC = () => {
  const navigate = useNavigate();
  const companies = useCompanies();
  const status = useTimeStatus();
  const proposals = useMigrationProposals(
    useFirmPermissions().can("manage_firm_settings")
  );
  const team = usePracticeTeamMembers();
  const firmName = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectPracticeName(s)
  );
  const [query, setQuery] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sort, setSort] = useState<CompanySort>("default");
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const lead = useMemo(
    () => new Map(team.options.map((o) => [o.uid, o.label])),
    [team.options]
  );
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortCompanies(
      (companies.data ?? []).filter(
        (c) =>
          !q ||
          c.name.toLowerCase().includes(q) ||
          c.client_no.toLowerCase().includes(q) ||
          c.entities.some((e) => e.legal_name.toLowerCase().includes(q))
      ),
      sort
    );
  }, [companies.data, query, sort]);
  const columnOptions = useMemo(
    () =>
      OPTIONAL_COLUMNS.filter(
        (c) => status.enabled || (c.id !== "export" && c.id !== "unbilled")
      ),
    [status.enabled]
  );
  const visible = useMemo(
    () =>
      new Set(columnOptions.map((c) => c.id).filter((id) => !hidden.has(id))),
    [columnOptions, hidden]
  );
  const proposed = proposals.data?.groups.length ?? 0;

  const allColumns: ColumnDef<CompanyListRow, unknown>[] = [
    {
      id: "company",
      header: "Company",
      meta: { className: "w-80 max-w-80" },
      cell: ({ row }) => {
        const c = row.original;
        return (
          <span className="flex min-w-0 items-center gap-2.5">
            <CompanyMark name={c.name} />
            <span className="flex min-w-0 flex-col">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-fg">{c.name}</span>
                {c.open_requests ? (
                  <StatusBadge tone="warn" data-testid={`requests-${c.id}`}>
                    {c.open_requests}{" "}
                    {c.open_requests === 1 ? "request" : "requests"}
                  </StatusBadge>
                ) : null}
              </span>
              {entitySummary(c) !== c.name ? (
                <span className="truncate text-sm text-fg-3">
                  {entitySummary(c)}
                </span>
              ) : null}
            </span>
          </span>
        );
      }
    },
    {
      id: "open",
      header: "Open Cases",
      meta: { isNumeric: true, className: "w-30" },
      cell: ({ row }) => row.original.open_cases
    },
    {
      id: "deadline",
      header: "Next Deadline",
      meta: { className: "w-40" },
      cell: ({ row }) => (
        <span
          className={cn(
            "truncate",
            isSoon(row.original.next_deadline) ? "text-warn" : "text-fg-3"
          )}
        >
          {formatDeadline(row.original.next_deadline)}
        </span>
      )
    },
    {
      id: "clientNo",
      header: "Client No.",
      meta: { className: "w-30" },
      cell: ({ row }) =>
        row.original.client_no ? (
          <span className="font-mono text-fg-2">{row.original.client_no}</span>
        ) : (
          <span className="text-fg-4">–</span>
        )
    },
    ...(status.enabled
      ? ([
          {
            id: "export",
            header: "Export",
            meta: { className: "w-35" },
            cell: ({ row }) =>
              row.original.export_format ? (
                <Tag mono>{formatLabel(row.original.export_format)}</Tag>
              ) : (
                <span className="text-fg-4">–</span>
              )
          },
          {
            id: "unbilled",
            header: "Unbilled",
            meta: { isNumeric: true, className: "w-30" },
            cell: ({ row }) =>
              row.original.unbilled ? (
                formatMoney(row.original.unbilled)
              ) : (
                <span className="text-fg-4">–</span>
              )
          }
        ] as ColumnDef<CompanyListRow, unknown>[])
      : []),
    {
      id: "lead",
      header: "Lead",
      cell: ({ row }) => {
        const uid = row.original.responsible_user_id;
        if (!uid) return <span className="text-fg-4">–</span>;
        const name = lead.get(uid) ?? "?";
        return (
          <Avatar size="sm" title={name}>
            <AvatarFallback>{initialsOf(name)}</AvatarFallback>
          </Avatar>
        );
      }
    }
  ];
  const columns = allColumns.filter(
    (c) => c.id === "company" || visible.has(c.id ?? "")
  );

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="companies-page"
    >
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Companies"
          count={companies.data?.length ?? null}
          description="Employers that send you their employees' cases."
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setImportOpen(true)}
              >
                <Download /> Import
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setNewOpen(true)}
              >
                <Plus /> New Company
              </Button>
            </>
          }
        />
        {proposed ? (
          <div
            className="flex items-center gap-3 rounded-lg bg-accent-soft px-3.5 py-2.5 ring-1 ring-accent-line"
            data-testid="migration-banner"
          >
            <Sparkles className="size-4 text-accent-seed" />
            <span className="flex-1 text-base text-fg">
              {proposed} {proposed === 1 ? "company" : "companies"} proposed
              from the employers on your existing cases. Nothing links until you
              review them.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(COMPANIES_REVIEW_PATH)}
            >
              Review
            </Button>
          </div>
        ) : null}
        <ListToolbar>
          <ListSearch
            aria-label="Search companies"
            placeholder="Search companies…"
            value={query}
            onValueChange={setQuery}
            className="w-55"
          />
          <span className="flex-1" />
          <DisplayOptions
            ordering={{
              options: SORT_OPTIONS,
              value: sort,
              onValueChange: (v) => setSort(v as CompanySort)
            }}
            properties={columnOptions.map((c) => ({
              ...c,
              visible: visible.has(c.id)
            }))}
            onPropertyChange={(id, on) =>
              setHidden((current) => {
                const next = new Set(current);
                if (on) next.delete(id);
                else next.add(id);
                return next;
              })
            }
          />
        </ListToolbar>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!companies.isLoading && !companies.data?.length ? (
          <EmptyState
            icon={Building2}
            title="No Companies Yet"
            description="Add the employers you file for. Their legal entities, FEINs, signatories and job descriptions fill every employee case, and their cases carry the company's billing IDs."
            action={
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setNewOpen(true)}>
                  <Plus /> New Company
                </Button>
                <Button size="sm" onClick={() => setImportOpen(true)}>
                  <Download /> Import
                </Button>
              </div>
            }
            className="py-16"
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            rowHeight="double"
            getRowId={(c) => c.id}
            getRowTestId={(c) => `company-row-${c.id}`}
            onRowClick={(c) => navigate(companyPath(c.id))}
            isLoading={companies.isLoading}
            className="overflow-visible"
          />
        )}

        <p className="max-w-160 px-6 py-4 text-sm text-fg-3">
          Individual clients stay under Clients. A person can belong to a
          company, and a company&apos;s employees keep their own client records,
          documents and portal.
        </p>
      </div>

      <NewCompanyDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(c) => navigate(companyPath(c.id))}
        firmName={firmName || null}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
};
